import { animate, style, transition, trigger } from "@angular/animations";
import { HttpClient } from "@angular/common/http";
import { Component, Inject, OnInit, ViewEncapsulation } from "@angular/core";
import { FormControl, FormGroup } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { IAlertConfig } from "@shared/models/alert";
import { AppAlertService } from "@shared/service/app/alert.service";
import { environment } from "environments/environment";
import { finalize, Observable, Subject, takeUntil, takeWhile, tap, timer } from "rxjs";

@Component({
  selector: "app-dialog-generar-reporte",
  templateUrl: "./dialog-generar-reporte.component.html",
  styleUrls: ["./dialog-generar-reporte.component.scss"],
  animations: [
    trigger('fadeIn', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('200ms ease-in', style({ opacity: 1 })),
      ]),
    ]),
  ],
  encapsulation: ViewEncapsulation.None,
  standalone: false
})
/**
 * INFO EN ARCHIVO readme.txt
 */
export class DialogGenerarReporteComponent implements OnInit {
  protected titulo: string;
  protected errores: string = "";
  protected filtros: { label: string; value: string }[];
  protected columnas: { label: string; attribute: string; form: FormControl }[];
  protected form: FormGroup;
  protected endpoint: string;
  protected endpointPdf: string;
  protected endpointExcel: string;
  protected payload: { columnas: { label: string; attribute: string }[]; titulo: string; excel: number; ids: number[]; filtros: { label: string; value: string }[], portrait: number };
  protected isDisabled: boolean = false;
  public showSpinner: boolean;
  public spinnerMessage: string = "Generando Reporte";
  showAlert = false;
  _alertConfig: IAlertConfig;
  private _unsubscribeAll: Subject<any> = new Subject<any>();

  constructor(
    private http: HttpClient, 
    public dialogRef: MatDialogRef<DialogGenerarReporteComponent>, 
    @Inject(MAT_DIALOG_DATA) public data: any, 
    private alertService: AppAlertService
  ) {
    this.payload = data.payload;
    this.titulo = data.payload.titulo;
    this.endpoint = data.endpoint;
    this.endpointPdf = data.endpointPdf;
    this.endpointExcel = data.endpointExcel;
    this.filtros = data.payload.filtros;
    this.columnas = data.payload.columnas;
    if (data?.payload && data.payload.errores) {
      this.titulo = data.payload.titulo;
      this.errores = data.payload.errores;
    }
  }

  ngOnInit(): void {
    this.subscribeAlert();
    this.form = new FormGroup({
      titulo: new FormControl(this.titulo),
      incluirFiltros: new FormControl(false),
      formatoPortaretrato: new FormControl(false),
      seleccionarTodo: new FormControl(false),
    });
    this.columnas.forEach((c) => {
      c.form = new FormControl(false);
    });
    this.onSeleccionarTodo(null, null, true);
  }

  subscribeAlert() {
      this.alertService.emitConfig.subscribe({
        next: (config: IAlertConfig) => {
          this.showAlert = true;
          this._alertConfig = {
            ...config,
            appearance: 'border',
          };
          let time = 2;
          timer(1000, 1000)
          .pipe(
            finalize(() => {
              this.showAlert = false;
              this._alertConfig = null;
            }),
            takeWhile(() => time > 0),
            takeUntil(this._unsubscribeAll),
            tap(() => (time -= 1))
          )
          .subscribe();
        },
      });
  }

  onSeleccionarTodo(event?: any, b = true, checkStart?) {
    if (event?.checked || checkStart) {
      if (checkStart) {
        this.form.get("seleccionarTodo").setValue(true);
      }
      this.columnas.forEach((c) => {
        c.form.setValue(true);
      });
    } else {
      this.columnas.forEach((c) => {
        c.form.setValue(false);
      });
    }
  }

  selectAll(event: any): void {
    event.target.select();
  }
  onChangeColumna(event: any) {
    let seleccionaTodo = true;
    this.columnas.forEach((c) => {
      if (!c.form.value) {
        seleccionaTodo = false;
      }
    });
    this.form.get("seleccionarTodo").setValue(seleccionaTodo);
  }
  onGenerarReporte(excel: number, endpoint: string) {
    this.showSpinner = true;
    this.isDisabled = true;
    let payload: { 
      columnas: { 
        label: string; 
        attribute: string 
      }[]; 
      titulo: string; 
      excel: number; 
      ids: number[]; 
      filtros: { 
        label: string; 
        value: string 
      }[],
      portrait: number,
    };
    let datos = this.payload;
    let columns = this.columasSeleccionadas();

    if (columns.length > 0) {
      datos.columnas = columns;
      if (this.form.get("incluirFiltros").value) {
        datos.filtros = this.filtros;
      } else {
        delete datos.filtros;
      }
      datos.portrait = this.form.get("formatoPortaretrato").value == true ? 1 : 0;

      datos.excel = excel;
      datos.titulo = this.form.get("titulo").value;
      payload = datos;

      this.obtenerReporte(payload, endpoint).subscribe(
        (response) => {
          const resultado = response.result;
          const path = excel === 1 
            ? environment.baseApi + resultado?.result 
            : resultado?.path;
          if ((resultado?.exito || resultado?.success) && path) {
            const tipoReporte = excel === 1 ? 'Excel' : 'PDF';
            this.alertService.success(`Reporte ${tipoReporte} generado con éxito.`);

            setTimeout(() => {
              if (excel === 1) {
                const link = document.createElement('a');
                link.href = path;
                link.download = path.split('/').pop()!;
                document.body.appendChild(link); 
                link.click();
                document.body.removeChild(link);
              } else {
                // Abrir PDF en nueva pestaña
                window.open(path, "_blank");
              }

              this.dialogRef.close();
            }, 1000);
          } else {
            this.alertService.error("No se pudo obtener el archivo. Verifique la respuesta del servidor.");
            console.error("⚠️ Respuesta sin éxito o sin path:", response);
          }

          this.isDisabled = false;
          this.showSpinner = false;
        },
        (error) => {
          console.error("❌ Error al generar el reporte:", error);
          this.alertService.error("Ocurrió un error al generar el reporte.");
          this.isDisabled = false;
          this.showSpinner = false;
        }
      );
    } else {
      this.alertService.warning("Debe seleccionar al menos una columna para imprimir.");
      this.isDisabled = false;
      this.showSpinner = false;
    }
  }

  obtenerReporte(payload: any, endpoint: string): Observable<any> {
    const url = `${environment.baseRest}/${endpoint}`;

    if (payload.excel === 1) {
      // GET para Excel: solo enviamos los IDs como query params
      return this.http.get(url);
    }

    // POST para PDF
    return this.http.post(url, payload);
  }

  columasSeleccionadas() {
    return this.columnas
      .filter((c) => c.form.value)
      .map((c) => {
        const { form, ...rest } = c;
        return rest;
      });
  }
}
